import { useReadContract } from "wagmi";
import { CONFIG } from "../config";
import type { Collection, StakingData } from "../types";

const stakingAbi = [
	{
		type: "function",
		name: "vesting",
		stateMutability: "view",
		inputs: [
			{ type: "address", name: "erc721" },
			{ type: "uint256", name: "tokenId" },
		],
		outputs: [
			{
				type: "tuple",
				components: [
					{ type: "bytes32", name: "uid" },
					{ type: "address", name: "erc721" },
					{ type: "uint256", name: "tokenId" },
					{ type: "bytes32", name: "poolId" },
					{ type: "uint256", name: "duration" },
					{ type: "uint256", name: "startDate" },
					{ type: "uint256", name: "endDate" },
					{ type: "uint256", name: "reward" },
				],
				name: "data",
			},
		],
	},
	{
		type: "function",
		name: "getPendingRewards",
		stateMutability: "view",
		inputs: [
			{ type: "address", name: "erc721" },
			{ type: "uint256", name: "tokenId" },
		],
		outputs: [{ type: "uint256" }],
	},
] as const;

export function useStakingData(collection: Collection, tokenId: string) {
	const { data: vestingData, isLoading: isVestingLoading } = useReadContract({
		address: CONFIG.STAKING_CONTRACT as `0x${string}`,
		abi: stakingAbi,
		functionName: "vesting",
		args: [collection.address as `0x${string}`, BigInt(tokenId || "0")],
		query: {
			enabled: Boolean(collection.address && tokenId),
		},
	});

	const { data: pendingRewards, isLoading: isRewardsLoading } = useReadContract({
		address: CONFIG.STAKING_CONTRACT as `0x${string}`,
		abi: stakingAbi,
		functionName: "getPendingRewards",
		args: [collection.address as `0x${string}`, BigInt(tokenId || "0")],
		query: {
			enabled: Boolean(collection.address && tokenId && vestingData?.startDate && vestingData.startDate > 0n),
		},
	});

	const isLoading = isVestingLoading || isRewardsLoading;

	if (!vestingData || !tokenId) {
		return {
			data: {
				isStaked: false,
				stakedAmount: 0n,
				pendingRewards: 0n,
			},
			isLoading,
		};
	}

	const now = BigInt(Math.floor(Date.now() / 1000));
	const startDate = vestingData.startDate;
	const endDate = vestingData.endDate;
	const isStaked = startDate > 0n;

	// Calculate pending rewards based on time elapsed
	let calculatedRewards = 0n;
	if (isStaked && startDate < now) {
		if (now >= endDate) {
			// If staking period is complete, return full reward
			calculatedRewards = vestingData.reward;
		} else {
			// Calculate partial rewards based on time elapsed
			const totalDuration = endDate - startDate;
			const elapsed = now - startDate;
			calculatedRewards = (vestingData.reward * elapsed) / totalDuration;
		}
	}

	const stakingData: StakingData = {
		isStaked,
		startDate: isStaked ? new Date(Number(startDate) * 1000) : undefined,
		endDate: isStaked ? new Date(Number(endDate) * 1000) : undefined,
		reward: vestingData.reward,
		stakedAmount: 1n, // NFT staking is always 1
		pendingRewards: pendingRewards || calculatedRewards,
	};

	return {
		data: stakingData,
		isLoading,
	};
}